import type { SaveButtonProps } from "components/SaveButton";

import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Breakpoint,
  ButtonProps,
  DialogContentProps,
  DialogProps,
  Grid,
  GridProps,
  GridSize,
  IconButton,
  Skeleton,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { ReactNode, useState } from "react";

import { FilePreview } from "./FilePreview";
import { FullScreenDocumentPreview } from "./FullScreenDocumentPreview";

type DocumentDialogContainerProps = Omit<DialogProps, "children" | "content"> & {
  /** Function called when the dialog is closed. Can be a simple callback or receive the close reason. */
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
      ) => void);
  /** Title displayed at the top of the dialog */
  title?: React.ReactNode;
  /** Optional subheader displayed below the title */
  subheader?: string;
  /** Optional description displayed in the content area of the dialog */
  description?: React.ReactNode;
  /** Optional element to display in the title bar to the left of the close button */
  titleBarActionElement?: React.ReactNode;
  /** Additional Primary Actions. These will display to the left of Cancel and Primary action button */
  additionalPrimaryActionElements?: React.ReactNode[];
  /** Secondary Actions will display on the bottom left of the action toolbar */
  secondaryActionElements?: React.ReactNode[];
  cancelButtonProps?: ButtonProps & { text?: string };
  /** Whether to show a primary action (e.g., Save button). Defaults to true. */
  noPrimaryAction?: boolean;
  /** Optionally provide a component to use as the primary action button. `primaryActionButtonProps` do not apply to this */
  primaryActionButton?: React.ReactNode;
  /** Props to configure the Save button (shown if `noPrimaryAction` is false) */
  primaryActionButtonProps?: SaveButtonProps;
  /** Optional styles to apply to the content area of the dialog */
  contentSx?: SxProps;
  /** Props to configure the form container (left column) */
  formContainerProps?: GridProps;
  /** Props to configure the DialogContent area of the dialog */
  DialogContentProps?: DialogContentProps;
  /** Form content to display in the left column */
  children: ReactNode;
  /** URL of the document to preview */
  previewUrl?: string;
  /** File extension of the document (e.g. "pdf", "xlsx", "docx") */
  fileExtension?: string;
  /** Loading state for the document preview */
  previewLoading?: boolean;
  /** Whether to show the preview box when preview fails or is unavailable */
  showPreviewBoxIfFailed?: boolean;
  /** Custom sx for the form container (left column) */
  formContainerSx?: SxProps<Theme>;
  /** Custom sx for the preview container (right column) */
  previewContainerSx?: SxProps<Theme>;
  /** Custom sx for the preview box itself */
  previewBoxSx?: SxProps<Theme>;
  /** Whether to hide the cancel button */
  hideCancelButton?: boolean;
  /** Function that returns the form column size based on whether preview is shown */
  formColumnSize?: Partial<Record<Breakpoint, GridSize>>;
  /** Function that returns the preview column size based on whether preview is shown */
  previewColumnSize?: Partial<Record<Breakpoint, GridSize>>;
};

export function DocumentDialogContainer({
  children,
  previewUrl,
  fileExtension,
  previewLoading = false,
  showPreviewBoxIfFailed = true,
  formContainerSx,
  previewContainerSx,
  previewBoxSx,
  onClose,
  title,
  subheader,
  description,
  titleBarActionElement,
  additionalPrimaryActionElements,
  secondaryActionElements,
  cancelButtonProps,
  noPrimaryAction,
  primaryActionButton,
  primaryActionButtonProps,
  contentSx,
  DialogContentProps,
  formContainerProps,
  hideCancelButton,
  formColumnSize,
  previewColumnSize,
  ...dialogProps
}: DocumentDialogContainerProps) {
  const theme = useTheme();
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);

  const showPreview = !!(previewUrl || showPreviewBoxIfFailed);

  // Default column sizes (old implementation)
  const defaultFormSize = showPreview ? { xs: 12, md: 6 } : { xs: 12, md: 12 };
  const defaultPreviewSize = { xs: 12, md: 6 };

  const formSize = formColumnSize ?? defaultFormSize;
  const previewSize = previewColumnSize ?? defaultPreviewSize;

  return (
    <Dialog
      {...dialogProps}
      hideCancelButton={hideCancelButton}
      onClose={onClose}
      title={title}
      subheader={subheader}
      description={description}
      titleBarActionElement={titleBarActionElement}
      additionalPrimaryActionElements={additionalPrimaryActionElements}
      secondaryActionElements={secondaryActionElements}
      cancelButtonProps={cancelButtonProps}
      noPrimaryAction={noPrimaryAction}
      primaryActionButton={primaryActionButton}
      primaryActionButtonProps={primaryActionButtonProps}
      contentSx={contentSx}
      DialogContentProps={{
        ...DialogContentProps,
        sx: {
          overflowY: "auto",
          ...DialogContentProps?.sx,
        },
      }}
    >
      <Grid container columnSpacing={3} alignItems="stretch" sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Column - Form */}
        <Grid
          container
          size={formSize}
          rowSpacing={1.5}
          columnSpacing={3}
          sx={{
            pr: showPreview ? theme.spacing(4) : 0,
            pt: theme.spacing(1), // or the header gets cut off
            overflowY: "auto",
            ...formContainerSx,
          }}
          {...formContainerProps}
        >
          {children}
        </Grid>

        {/* Right Column - Document Preview */}
        {showPreview && (
          <Grid
            size={previewSize}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              ...previewContainerSx,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Preview
              </Typography>
              {previewUrl && (
                <IconButton
                  size="small"
                  onClick={() => setFullScreenPreviewOpen(true)}
                  sx={{ ml: 0.5 }}
                  aria-label="Open full-screen preview"
                >
                  <Visibility sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            {previewLoading ? (
              <Box
                sx={{
                  minHeight: "400px",
                  width: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(0.5),
                  p: 2,
                  ...previewBoxSx,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  sx={{
                    borderRadius: theme.spacing(0.5),
                  }}
                />
              </Box>
            ) : previewUrl ? (
              <Box
                sx={{
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ position: "absolute", inset: 0 }}>
                  <FilePreview fileUrl={previewUrl} fileExtension={fileExtension} />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(0.5),
                  ...previewBoxSx,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No preview available
                </Typography>
              </Box>
            )}
            {previewUrl && (
              <FullScreenDocumentPreview
                open={fullScreenPreviewOpen}
                onClose={() => setFullScreenPreviewOpen(false)}
                fileUrl={previewUrl}
                fileExtension={fileExtension}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Dialog>
  );
}
